import React, { useEffect, useRef, useState } from 'react';

const track = {
    name: "",
    album: {
        images: [ { url: "" } ]
    },
    artists: [ { name: "" } ]
};

function WebPlayback(props) {
    const { token, getOAuthToken, onTrackChange, onPausedChange, onPlayerReady, onDeviceId } = props;
    const [is_paused, setPaused] = useState(false);
    const [current_track, setTrack] = useState(track);

    const spotifyPlayerRef = useRef(null);
    const tokenRef = useRef(token);
    
    // Keep tokenRef in sync with the latest token
    useEffect(() => {
        tokenRef.current = token;
    }, [token]);

    // Notify parent of track changes
    useEffect(() => {
        if (onTrackChange) {
            onTrackChange(current_track);
        }
    }, [current_track, onTrackChange]);

    // Notify parent of pause state changes
    useEffect(() => {
        if (onPausedChange) {
            onPausedChange(is_paused);
        }
    }, [is_paused, onPausedChange]);

    useEffect(() => {
        if (!token) return;

        let cancelled = false;

        const ensureScriptLoaded = () => {
            // SDK already available
            if (window.Spotify && window.Spotify.Player) return Promise.resolve();

            // Script already requested
            const existing = document.querySelector('script[src="https://sdk.scdn.co/spotify-player.js"]');
            if (existing) {
                return new Promise((resolve) => {
                    const prev = window.onSpotifyWebPlaybackSDKReady;
                    window.onSpotifyWebPlaybackSDKReady = () => {
                        prev && prev();
                        resolve();
                    };
                });
            }

            // Request script
            const script = document.createElement("script");
            script.src = "https://sdk.scdn.co/spotify-player.js";
            script.async = true;
            document.body.appendChild(script);

            return new Promise((resolve) => {
                const prev = window.onSpotifyWebPlaybackSDKReady;
                window.onSpotifyWebPlaybackSDKReady = () => {
                    prev && prev();
                    resolve();
                };
            });
        };

        const initPlayer = async () => {
            await ensureScriptLoaded();
            if (cancelled) return;

            // Recreate player when token changes
            if (spotifyPlayerRef.current) {
                try { spotifyPlayerRef.current.disconnect(); } catch {}
                spotifyPlayerRef.current = null;
            }

            const newPlayer = new window.Spotify.Player({
                name: 'PomoDucky Player',
                getOAuthToken: cb => {
                    // Use the callback from props if available, otherwise use current token
                    if (getOAuthToken) {
                        getOAuthToken(cb);
                    } else {
                        // Fallback to the current token ref
                        cb(tokenRef.current);
                    }
                },
                volume: 0.5
            });
            spotifyPlayerRef.current = newPlayer;

            newPlayer.addListener('ready', ({ device_id }) => {
                console.log('Ready with Device ID', device_id);
                if (!cancelled && onDeviceId) {
                    onDeviceId(device_id);
                }
                // Notify parent that player is ready
                if (!cancelled && onPlayerReady) {
                    onPlayerReady({
                        togglePlay: () => newPlayer.togglePlay(),
                        nextTrack: () => newPlayer.nextTrack(),
                        previousTrack: () => newPlayer.previousTrack(),
                        setVolume: (volume) => newPlayer.setVolume(volume),
                        getCurrentState: () => newPlayer.getCurrentState(),
                        connect: () => newPlayer.connect(),
                        disconnect: () => newPlayer.disconnect(),
                        addListener: (event, callback) => newPlayer.addListener(event, callback),
                        removeListener: (event, callback) => newPlayer.removeListener(event, callback),
                        _player: newPlayer
                    });
                }
            });
            newPlayer.addListener('not_ready', ({ device_id }) => {
                console.log('Device ID has gone offline', device_id);
            });
            newPlayer.addListener('authentication_error', ({ message }) => {
                console.error('Spotify authentication error:', message);
                // The SDK will automatically call getOAuthToken to get a fresh token
            });
            newPlayer.addListener('account_error', ({ message }) => {
                console.error('Spotify account error:', message);
            });
            newPlayer.addListener('initialization_error', ({ message }) => {
                console.error('Spotify initialization error:', message);
            });
            newPlayer.addListener('playback_error', ({ message }) => {
                console.error('Spotify playback error:', message);
            });
            newPlayer.addListener('player_state_changed', (state => {
                if (!state) {
                    return;
                }
                setTrack(state.track_window.current_track);
                setPaused(state.paused);
            }));
            newPlayer.connect();
        };

        initPlayer();

        return () => {
            cancelled = true;
            if (spotifyPlayerRef.current) {
                try { spotifyPlayerRef.current.disconnect(); } catch {}
                spotifyPlayerRef.current = null;
            }
        };
    }, [token, getOAuthToken, onPlayerReady, onDeviceId]);

    return (
        <>
            {/* Headless SDK component: UI is handled by SpotifyControls */}
        </>
    );
}

export default WebPlayback;
